import React, { useState, useEffect } from 'react';
import Navbar from '../../components/Navbar';
import Footer from '../../components/Footer';
import { GitCompare, Info, Plus, X, Download, TrendingUp, TrendingDown } from 'lucide-react';

const SchemeComparisonPage = () => {
  const [amcList, setAmcList] = useState([]);
  const [categories, setCategories] = useState([]);
  const [schemeData, setSchemeData] = useState([]);
  const [selectedSchemes, setSelectedSchemes] = useState([]);
  const [loading, setLoading] = useState(false);
  const [selectedAmc, setSelectedAmc] = useState('all');
  const [selectedCategory, setSelectedCategory] = useState('all');

  const API_URL = process.env.REACT_APP_BACKEND_URL;

  useEffect(() => {
    fetchInitialData();
  }, []);

  useEffect(() => {
    fetchSchemeData();
  }, [selectedAmc, selectedCategory]);

  const fetchInitialData = async () => {
    try {
      const [amcRes, catRes] = await Promise.all([
        fetch(`${API_URL}/api/research/amc-list`),
        fetch(`${API_URL}/api/research/scheme-categories`)
      ]);
      
      const amcData = await amcRes.json();
      const catData = await catRes.json();
      
      if (amcData.status === 'success') setAmcList(amcData.data);
      if (catData.status === 'success') setCategories(catData.data);
    } catch (error) {
      console.error('Error fetching initial data:', error);
    }
  };

  const fetchSchemeData = async () => {
    setLoading(true);
    try {
      const res = await fetch(
        `${API_URL}/api/research/scheme-performance?amc=${selectedAmc}&category=${selectedCategory}`
      );
      const data = await res.json();
      if (data.status === 'success') {
        setSchemeData(data.data);
      }
    } catch (error) {
      console.error('Error fetching scheme data:', error);
    }
    setLoading(false);
  };

  const addScheme = (scheme) => {
    if (selectedSchemes.length < 10 && !selectedSchemes.find(s => s.scheme_name === scheme.scheme_name)) {
      setSelectedSchemes([...selectedSchemes, scheme]);
    }
  };

  const removeScheme = (schemeName) => {
    setSelectedSchemes(selectedSchemes.filter(s => s.scheme_name !== schemeName));
  };

  const formatReturn = (value) => {
    if (value === null || value === undefined) return '-';
    const num = parseFloat(value);
    const color = num >= 0 ? 'text-green-400' : 'text-red-400';
    return <span className={color}>{num.toFixed(2)}%</span>;
  };

  const availableSchemes = schemeData.filter(
    s => !selectedSchemes.find(sel => sel.scheme_name === s.scheme_name)
  );

  return (
    <div className="min-h-screen bg-gradient-to-br from-black via-gray-900 to-black">
      <Navbar />
      
      <div className="pt-28 pb-20 px-4 sm:px-6">
        <div className="max-w-7xl mx-auto">
          {/* Header */}
          <div className="text-center mb-10">
            <div className="inline-flex items-center justify-center w-14 h-14 bg-gradient-to-r from-indigo-500 to-purple-500 rounded-2xl mb-4">
              <GitCompare className="w-7 h-7 text-white" />
            </div>
            <h1 className="text-3xl sm:text-4xl lg:text-5xl font-bold text-white mb-4">
              Scheme Comparison
            </h1>
            <div className="bg-indigo-500/10 border border-indigo-500/30 rounded-xl p-4 max-w-3xl mx-auto">
              <div className="flex items-start gap-3">
                <Info className="w-5 h-5 text-indigo-400 flex-shrink-0 mt-0.5" />
                <p className="text-white/80 text-sm text-left">
                  Compare multiple mutual fund schemes side-by-side. Select up to 10 schemes to compare their performance, 
                  returns, and key metrics.
                </p>
              </div>
            </div>
          </div>

          {/* Selected Schemes */}
          {selectedSchemes.length > 0 && (
            <div className="mb-6">
              <p className="text-white/70 text-sm mb-3">Selected Schemes ({selectedSchemes.length}/10)</p>
              <div className="flex flex-wrap gap-2">
                {selectedSchemes.map((scheme, idx) => (
                  <div key={idx} className="flex items-center gap-2 px-3 py-2 bg-indigo-500/20 border border-indigo-500/30 rounded-lg">
                    <span className="text-white text-sm truncate max-w-[200px]">{scheme.scheme_name.split(' - ')[0]}</span>
                    <button onClick={() => removeScheme(scheme.scheme_name)} className="text-white/50 hover:text-red-400">
                      <X className="w-4 h-4" />
                    </button>
                  </div>
                ))}
              </div>
            </div>
          )}

          {/* Filters & Add Scheme */}
          <div className="bg-white/5 backdrop-blur-xl rounded-2xl p-6 border border-white/10 mb-6">
            <div className="grid md:grid-cols-3 gap-4">
              <div>
                <label className="text-white/70 text-sm mb-2 block">Select Fund House</label>
                <select
                  value={selectedAmc}
                  onChange={(e) => setSelectedAmc(e.target.value)}
                  className="w-full px-4 py-3 bg-white/10 border border-white/20 rounded-lg text-white focus:outline-none focus:border-indigo-500 appearance-none cursor-pointer"
                >
                  <option value="all" className="bg-gray-900">All Fund Houses</option>
                  {amcList.map((amc, idx) => (
                    <option key={idx} value={amc} className="bg-gray-900">{amc}</option>
                  ))}
                </select>
              </div>

              <div>
                <label className="text-white/70 text-sm mb-2 block">Select Scheme Type</label>
                <select
                  value={selectedCategory}
                  onChange={(e) => setSelectedCategory(e.target.value)}
                  className="w-full px-4 py-3 bg-white/10 border border-white/20 rounded-lg text-white focus:outline-none focus:border-indigo-500 appearance-none cursor-pointer"
                >
                  {categories.map((cat, idx) => (
                    <option key={idx} value={cat.value} className="bg-gray-900">{cat.label}</option>
                  ))}
                </select>
              </div>

              <div>
                <label className="text-white/70 text-sm mb-2 block">Add Scheme to Compare</label>
                <select
                  onChange={(e) => {
                    const scheme = availableSchemes.find(s => s.scheme_name === e.target.value);
                    if (scheme) addScheme(scheme);
                    e.target.value = '';
                  }}
                  className="w-full px-4 py-3 bg-white/10 border border-white/20 rounded-lg text-white focus:outline-none focus:border-indigo-500 appearance-none cursor-pointer"
                  disabled={selectedSchemes.length >= 10}
                >
                  <option value="" className="bg-gray-900">Select a scheme to add</option>
                  {availableSchemes.map((scheme, idx) => (
                    <option key={idx} value={scheme.scheme_name} className="bg-gray-900">{scheme.scheme_name}</option>
                  ))}
                </select>
              </div>
            </div>
          </div>

          {/* Comparison Table */}
          {selectedSchemes.length > 0 ? (
            <div className="bg-white/5 backdrop-blur-xl rounded-2xl border border-white/10 overflow-hidden">
              <div className="p-4 border-b border-white/10 flex justify-between items-center">
                <p className="text-white font-semibold">Comparison Results</p>
                <button className="flex items-center gap-2 px-4 py-2 bg-indigo-500/20 border border-indigo-500/30 rounded-lg text-indigo-400 hover:bg-indigo-500/30 transition-colors text-sm">
                  <Download className="w-4 h-4" />
                  Export Report
                </button>
              </div>
              <div className="overflow-x-auto">
                <table className="w-full">
                  <thead>
                    <tr className="bg-white/5">
                      <th className="text-left py-4 px-4 text-white/70 font-medium text-sm">Metric</th>
                      {selectedSchemes.map((scheme, idx) => (
                        <th key={idx} className="text-center py-4 px-4 text-white/70 font-medium text-sm min-w-[150px]">
                          <div className="truncate">{scheme.scheme_name.split(' - ')[0]}</div>
                          <div className="text-xs text-white/40 mt-1 truncate">{scheme.amc.replace(' Mutual Fund', '')}</div>
                        </th>
                      ))}
                    </tr>
                  </thead>
                  <tbody>
                    <tr className="border-b border-white/5">
                      <td className="py-4 px-4 text-white/70 text-sm font-medium flex items-center gap-2">
                        <TrendingUp className="w-4 h-4 text-blue-400" />
                        1 Year Return
                      </td>
                      {selectedSchemes.map((scheme, idx) => (
                        <td key={idx} className="text-center py-4 px-4 font-mono text-sm">
                          {formatReturn(scheme['1y'])}
                        </td>
                      ))}
                    </tr>
                    <tr className="border-b border-white/5">
                      <td className="py-4 px-4 text-white/70 text-sm font-medium flex items-center gap-2">
                        <TrendingUp className="w-4 h-4 text-green-400" />
                        3 Year Return
                      </td>
                      {selectedSchemes.map((scheme, idx) => (
                        <td key={idx} className="text-center py-4 px-4 font-mono text-sm">
                          {formatReturn(scheme['3y'])}
                        </td>
                      ))}
                    </tr>
                    <tr className="border-b border-white/5">
                      <td className="py-4 px-4 text-white/70 text-sm font-medium flex items-center gap-2">
                        <TrendingUp className="w-4 h-4 text-purple-400" />
                        5 Year Return
                      </td>
                      {selectedSchemes.map((scheme, idx) => (
                        <td key={idx} className="text-center py-4 px-4 font-mono text-sm">
                          {formatReturn(scheme['5y'])}
                        </td>
                      ))}
                    </tr>
                    <tr className="border-b border-white/5">
                      <td className="py-4 px-4 text-white/70 text-sm font-medium flex items-center gap-2">
                        <TrendingUp className="w-4 h-4 text-orange-400" />
                        10 Year Return
                      </td>
                      {selectedSchemes.map((scheme, idx) => (
                        <td key={idx} className="text-center py-4 px-4 font-mono text-sm">
                          {formatReturn(scheme['10y'])}
                        </td>
                      ))}
                    </tr>
                    <tr className="border-b border-white/5">
                      <td className="py-4 px-4 text-white/70 text-sm font-medium flex items-center gap-2">
                        <TrendingUp className="w-4 h-4 text-cyan-400" />
                        Since Inception
                      </td>
                      {selectedSchemes.map((scheme, idx) => (
                        <td key={idx} className="text-center py-4 px-4 font-mono text-sm">
                          {formatReturn(scheme.inception)}
                        </td>
                      ))}
                    </tr>
                    <tr>
                      <td className="py-4 px-4 text-white/70 text-sm font-medium">Category</td>
                      {selectedSchemes.map((scheme, idx) => (
                        <td key={idx} className="text-center py-4 px-4 text-white/60 text-xs">
                          {scheme.category?.replace(/_/g, ' ').replace(/\b\w/g, l => l.toUpperCase())}
                        </td>
                      ))}
                    </tr>
                  </tbody>
                </table>
              </div>
            </div>
          ) : (
            <div className="bg-white/5 backdrop-blur-xl rounded-2xl p-12 border border-white/10 text-center">
              <GitCompare className="w-16 h-16 text-white/20 mx-auto mb-4" />
              <p className="text-white/50">Select schemes from the dropdown above to start comparing</p>
              <p className="text-white/30 text-sm mt-2">You can compare up to 10 schemes at a time</p>
            </div>
          )}

          {/* Disclaimer */}
          <div className="mt-6 text-center">
            <p className="text-white/40 text-xs">
              * Past performance is not indicative of future results. All returns shown are CAGR.
            </p>
          </div>
        </div>
      </div>

      <Footer />
    </div>
  );
};

export default SchemeComparisonPage;
